import { SelectInput } from '@/src/common/components/elements/Forms/SelectWithLabel'
import { useRecipientDeliveryContext } from '@/src/common/contexts/RecipientDeliveryContext'
import useCheckFedexAvailability from '@/src/common/hooks/useCheckFedexAvailability'
import { clearFormErrorForField } from '@/src/common/utilities/clearFormErrorForField'
import { inputHasError } from '@/src/common/utilities/inputHasError'
import { FormErrorDisplay } from '@/src/modules/build-a-card/FormErrorDisplay'
import {
    FieldWrapper,
    SelectError,
} from '@/src/modules/smarty/StyledComponents'
import { useEffect, useState } from 'react'
import { ClipLoader } from 'react-spinners'
import { styled } from '../../stitches.config'

const LoadingText = styled('div', {
    display: 'flex',
    ai: 'center',
    gap: 15,
    '& p': {
        color: '$dark',
        mb: 15,
    },
})

interface IDeliveryForm {
    physicalDeliveryOptions: any
    lineItemCount: number
}

function getDeliveryLabelsForSelectDisplay({
    fedexDeliveryData,
    allDeliveryOptions,
    lineItemCount,
    setAvailableDeliveryOptions,
}: {
    allDeliveryOptions: any[]
    fedexDeliveryData: string[]
    lineItemCount: number
    setAvailableDeliveryOptions: Function
}) {
    let listOfAvailableDeliveryMethods: any[] = []

    allDeliveryOptions.forEach((option) => {
        const revisedShippingName = option.delivery_option_name
            .replaceAll(' ', '-')
            .replace('1st', 'first')
            .toLowerCase()

        // always include usps in the list of delivery methods
        if (revisedShippingName.includes('usps')) {
            listOfAvailableDeliveryMethods.push(option)
        }

        // only include fedex in the list if its specific name is present
        if (fedexDeliveryData?.includes(revisedShippingName)) {
            listOfAvailableDeliveryMethods.push(option)
        }
    })

    const deliveryOptions = listOfAvailableDeliveryMethods
        ?.filter((deliveryOption: any) => {
            // only include usps if the order contains ONE physical item
            const isUspsEligible: boolean =
                lineItemCount === 1 &&
                deliveryOption?.carrier?.carrier_name.toLowerCase() === 'usps'
            const isFedexEligible: boolean =
                deliveryOption.carrier?.carrier_name.toLowerCase() === 'fedex'

            return Boolean(isUspsEligible || isFedexEligible)
        })
        ?.sort((a: any, b: any) => {
            const aVal = parseFloat(a.rate)
            const bVal = parseFloat(b.rate)
            return aVal - bVal
        })
        ?.map((deliveryOption: any) => {
            const {
                rate,
                delivery_option_name,
                delivery_option_eta,
                delivery_option_uuid,
            } = deliveryOption
            return {
                label: `$${parseFloat(rate).toFixed(
                    2
                )} ${delivery_option_name} (${delivery_option_eta})`,
                value: delivery_option_uuid,
            }
        })

    setAvailableDeliveryOptions(deliveryOptions)
}

export const SmartyShippingMethodField = ({
    lineItemCount,
    physicalDeliveryOptions,
}: IDeliveryForm) => {
    const {
        isFullAddress,
        delivery,
        setDelivery,
        addressPayload,
        formErrors,
        setFormErrors,
        selectError,
        setSelectError,
        address1,
        address2,
        city,
        state,
        zip,
    } = useRecipientDeliveryContext()

    const {
        mutate: checkFedexAvailability,
        data: fedexDeliveryData,
        isLoading: checkFedexIsLoading,
    } = useCheckFedexAvailability()
    const [availableDeliveryOptions, setAvailableDeliveryOptions] = useState<
        any[]
    >([])
    const [debounceTimer, setDebounceTimer] = useState<any>()

    // if a name and address have been entered, clear the error and request data
    // if not, clear the available delivery options and selected delivery option
    useEffect(() => {
        clearTimeout(debounceTimer)

        const timer = setTimeout(() => {
            if (isFullAddress) {
                setSelectError('')
                checkFedexAvailability(addressPayload)
            } else {
                setAvailableDeliveryOptions([])
                setDelivery('')
            }
        }, 1000)

        setDebounceTimer(timer)
    }, [address1, address2, city, state, zip, addressPayload])

    // use the fedex data to determine final list of available shipping methods
    useEffect(() => {
        if (fedexDeliveryData) {
            getDeliveryLabelsForSelectDisplay({
                lineItemCount: lineItemCount,
                setAvailableDeliveryOptions: setAvailableDeliveryOptions,
                allDeliveryOptions: physicalDeliveryOptions,
                fedexDeliveryData: fedexDeliveryData,
            })
        }
    }, [fedexDeliveryData, lineItemCount, physicalDeliveryOptions])

    if (checkFedexIsLoading) {
        return (
            <FieldWrapper>
                <LoadingText>
                    <p>Loading shipping methods</p>
                    <ClipLoader size={20} />
                </LoadingText>
                {selectError ? (
                    <SelectError>{selectError}</SelectError>
                ) : (
                    <FormErrorDisplay
                        formErrors={formErrors}
                        path={'delivery'}
                    />
                )}
            </FieldWrapper>
        )
    }

    return (
        <FieldWrapper style={{ zIndex: '0' }}>
            <SelectInput
                label={'Shipping method'}
                className={'shipping-method'}
                value={delivery}
                // The delivery options have several ways to get a showable name here. One way is to use
                // the "order_delivery_option_name" which is intended to be something that is an arbitrary
                // string that an admin can configure. Another way is to compose the value from:
                // carrier.carrier_name + service.service_name
                options={availableDeliveryOptions ?? []}
                onValueChange={(value: string) => {
                    setDelivery(value)
                    clearFormErrorForField({
                        path: 'delivery',
                        formErrors: formErrors,
                        setFormErrors: setFormErrors,
                    })
                }}
                onOpenChange={() => {
                    if (!isFullAddress) {
                        setSelectError(
                            'Please complete all required delivery fields before selecting a shipping method'
                        )
                    }
                }}
                hasError={inputHasError(formErrors, 'delivery') || selectError}
            />
            {selectError ? (
                <SelectError>{selectError}</SelectError>
            ) : (
                <FormErrorDisplay formErrors={formErrors} path={'delivery'} />
            )}
        </FieldWrapper>
    )
}
